import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Box,
  Grid,
  styled,
  Dialog,
  TextField,
  DialogTitle,
  DialogContent,
  LinearProgress,
  FormControl,
  OutlinedInput,
  InputAdornment,
  FormHelperText,
  Typography,
  Slider,
  AlertColor
} from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import { AlertDialog, DialogResult } from 'src/components/analytics';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import _ from 'lodash';
import { MultiSelect } from 'react-multi-select-component';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
import MODULE_TO_PAGE_MAP from '../../../home/types/simulation-module-to-page-map';
import { useAppDispatch, useAppSelector } from '../../../../../../hooks';
import {
  resetDuplicateSku,
  setInputFieldValues
} from '../../store/innovation-slice';
import { duplicateBulkSku } from '../../store/innovation-slice-async-thunks';
import { getBulkDuplicatePayload } from '../../utils/utils';
import './index.scss';
import { formatNumber } from '../../../pricing-simulator';
import { ISkuObject } from '../../types/sku-popup-model';
import DuplicateFilters from './duplicate-filters/duplicate-filters';
import SkuTableContent from './sku-list-table/sku-table-content';
import SnackbarComponent from '../snackbar/snackbar';

export default function DuplicateSku({
  isDupDialogOpen,
  setDupDialog
}: {
  isDupDialogOpen: boolean;
  setDupDialog: (isSnackbarToOpen, snackbarText, snackbarType) => void;
}) {
  // app dispatch for dispatching actions
  const appDispatch = useAppDispatch();
  // Getting state from innovation
  const {
    appliedFilters,
    isSkuCRUDLoading,
    duplicateSkuCountMappingHash,
    isDuplicateEnabled
  } = useAppSelector((state) => state.innovation);

  // local state for snackbar open/close
  const [isSnackbarOpen, setSnackbar] = useState(false);
  const [snackbarContent, setSnackbarContent] = useState<{
    snackbarText: string;
    snackbarType: AlertColor;
  }>({
    snackbarText: '',
    snackbarType: 'success'
  });

  // local state for duplicate dialog open state
  const [isDuplicateDialogOpen, setDialog] = useState(true);

  //  local state for confirmation dialogue
  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  // getting user, scenario and source details from useModuleMetadata hooks
  const { moduleId, sourceId, scenarioId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.INNOVATION
  });

  // handler for closing duplicate sku popup
  const handleClose = (
    event,
    reason,
    isSnackbarToOpen,
    snackbarText = '',
    snackbarType = ''
  ) => {
    if (reason && reason === 'backdropClick') return;
    setDialog(false);
    setDupDialog(isSnackbarToOpen, snackbarText, snackbarType);
    // resetting the duplicate skus
    appDispatch(resetDuplicateSku());
  };

  const handleCloseOnIconClick = () => {
    setConfirmationDialogOpen(true);
  };

  // function to handle confirmation dialogue close
  const handleConfirmationDialogClose = (result: DialogResult) => {
    if (result === DialogResult.YES) {
      // eslint-disable-next-line no-restricted-globals
      handleClose(event, 'reason', false);
    }
    // setting confirmation dialog to false
    setConfirmationDialogOpen(false);
    // setDialog(true);
  };

  /**
   * handler for submit bulk sku
   */
  const submitBulkSku = () => {
    appDispatch(
      duplicateBulkSku({
        sourceID: sourceId,
        moduleID: moduleId,
        scenarioID: Number(scenarioId),
        bulkSkus: getBulkDuplicatePayload(duplicateSkuCountMappingHash),
        filters: appliedFilters
      })
    ).then((event) => {
      if (event.meta.requestStatus === 'rejected') {
        setSnackbarContent({
          // eslint-disable-next-line dot-notation
          snackbarText: event['error'].message,
          snackbarType: 'error'
        });
        setSnackbar(true);
      } else {
        // eslint-disable-next-line no-restricted-globals
        handleClose(
          event,
          'reason',
          true,
          'Bulk SKU duplicated successfully',
          'success'
        );
      }
    });
  };

  /**
   * handlet for resetting bulk sku input fields
   */
  const resetInputFields = () => {
    // resetting the duplicate skus
    appDispatch(resetDuplicateSku());
  };

  return (
    <>
      <Dialog
        fullWidth
        maxWidth='lg'
        open={isDupDialogOpen}
        // onClose={(event) => handleClose(event, 'reason', false)}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        disableEscapeKeyDown
      >
        {isSkuCRUDLoading && <LinearProgress />}
        <DialogTitle id='alert-dialog-title' px={2}>
          Duplicate SKU
          <IconButton
            aria-label='close'
            onClick={handleCloseOnIconClick}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DuplicateFilters />
          <SkuTableContent />
          <AlertDialog
            closeHandler={handleConfirmationDialogClose}
            isDialogOpen={isConfirmationDialogOpen}
            message='Inputs will be reset and all changes to will be lost. Continue?'
            title='Cancel'
          />
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={resetInputFields}>
            Reset
          </Button>
          <Button
            color='primary'
            variant='contained'
            onClick={submitBulkSku}
            autoFocus
            disabled={!isDuplicateEnabled}
          >
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>

      <SnackbarComponent
        isSnackbarOpen={isSnackbarOpen}
        setSnackbar={setSnackbar}
        snackbarText={snackbarContent.snackbarText}
        snackbarType={snackbarContent.snackbarType}
      />
    </>
  );
}
